import { useEffect, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COURSES, getCoursePath, getCoursesPath } from "../../courses/config";
import { getLanguageFromPath } from "../../languages";
import {
  CoursesNavItem,
  CoursesNavTriggerLink,
  CoursesDropdown,
  CoursesDropdownPanel,
  CoursesDropdownLink,
  CoursesMobileList,
  CoursesMobileExpandButton,
  CoursesMobileTopRow,
  CoursesMobileMainLink,
  CoursesMobileSubmenu,
  CoursesMobileSubLink,
} from "./styles";

type CoursesNavProps = {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
  drawerOpen?: boolean;
};

const CoursesNav = ({
  variant = "desktop",
  onNavigate,
  drawerOpen = false,
}: CoursesNavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const currentLanguage = getLanguageFromPath(location.pathname);

  useEffect(() => {
    if (!drawerOpen) {
      setMobileExpanded(false);
    }
  }, [drawerOpen]);

  const handleNavigate = () => {
    setDesktopOpen(false);
    setMobileExpanded(false);
    onNavigate?.();
  };

  if (variant === "mobile") {
    return (
      <CoursesMobileList>
        <CoursesMobileTopRow>
          <CoursesMobileMainLink
            to={getCoursesPath(currentLanguage)}
            onClick={handleNavigate}
          >
            {t("courses.nav")}
          </CoursesMobileMainLink>
          <CoursesMobileExpandButton
            type="button"
            aria-label={t("courses.mobile.toggle")}
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            <DownOutlined />
          </CoursesMobileExpandButton>
        </CoursesMobileTopRow>
        {mobileExpanded && (
          <CoursesMobileSubmenu>
            {COURSES.map((course) => (
              <CoursesMobileSubLink
                key={course.slug}
                to={getCoursePath(course.slug, currentLanguage)}
                onClick={handleNavigate}
              >
                {t(course.labelKey)}
              </CoursesMobileSubLink>
            ))}
          </CoursesMobileSubmenu>
        )}
      </CoursesMobileList>
    );
  }

  return (
    <CoursesNavItem
      onMouseEnter={() => setDesktopOpen(true)}
      onMouseLeave={() => setDesktopOpen(false)}
    >
      <CoursesNavTriggerLink
        to={getCoursesPath(currentLanguage)}
        onClick={handleNavigate}
      >
        {t("courses.nav")}
      </CoursesNavTriggerLink>
      <CoursesDropdown $open={desktopOpen}>
        <CoursesDropdownPanel>
          {COURSES.map((course) => (
            <CoursesDropdownLink
              key={course.slug}
              to={getCoursePath(course.slug, currentLanguage)}
              onClick={handleNavigate}
            >
              {t(course.labelKey)}
            </CoursesDropdownLink>
          ))}
        </CoursesDropdownPanel>
      </CoursesDropdown>
    </CoursesNavItem>
  );
};

export default CoursesNav;
